import {getTestProps} from '@benchling/js-test-utils';
import React from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {getNonBatchContainerContents} from 'constants/SampleConstants';
import {CheckOutStatus, CheckOutStatusInfo, SampleStatusItem} from 'constants/SampleStatusConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import ExcludeByTypename from 'graphql-client/ExcludeByTypename';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import {
  doesCurrentUserHaveAccessForItem,
  doesItemHaveBlockingStatusWarning,
  getCICOWarningText,
  getRestrictionAccessDisplayText,
  isItemAvailable,
} from 'inventory/sample-status/SampleStatusPresentationUtils';
import {GridPreviewQuery} from 'samples/grids/__generated/GridPreviewQuery';
import {formatGridCoordinates} from 'samples/SampleUtil';
import {PlateSchemaType} from 'samples/storables/StorableSchemaConstants';
import {getLegacyMeasurementString} from 'samples/transfers/TransferUtil';
import {isPlateSchemaGraphQL} from 'schemas/SchemaPredicates';
import {
  GRAY,
  GRAY_LIGHT,
  GRAY_LIGHTER,
  GREEN_LIGHTER,
  ORANGE_LIGHTER,
  RED_LIGHTEST,
  WHITE,
} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {omit} from 'util/omitKey';

import {StorableChipSummaryGraphQL} from '../../../graphql-fragments/inventory/__generated/StorableChipSummaryGraphQL';

type GridCellsInfo = ExtractByTypename<GridPreviewQuery['storable'], 'BoxNode'>;

type GridCell = NonNullable<GridCellsInfo['cells'][number][number]>;

type Container = ExtractByTypename<GridCell['container'], 'ContainerNode'>;

type ContainerContent = Container['contents'][number];

type ContainedItem = ExcludeByTypename<ContainerContent['containedItem'], 'BatchNode'>;

type BoxSchemaGraphQL = ExtractByTypename<StorableChipSummaryGraphQL, 'BoxNode'>['storableSchema'];

type PlateSchemaGraphQL = ExtractByTypename<StorableChipSummaryGraphQL, 'PlateNode'>['storableSchema'];

export type GridPreviewData = GridCellsInfo & {storableSchema: BoxSchemaGraphQL | PlateSchemaGraphQL};

interface ContentWithoutBatches extends ContainerContent {
  containedItem: ContainedItem;
}

export default function GridPreview(props: {
  grid: GridPreviewData;
  heightPx: number;
  widthPx: number;
}): JSX.Element {
  function isAccessibleContainer(
    thing?: {__typename: 'InaccessibleInfoNode'} | Container | null
  ): thing is Container {
    return thing != null && thing.__typename === 'ContainerNode';
  }

  function accessibleContainer(thing?: {__typename: 'InaccessibleInfoNode'} | Container | null): Container {
    assert(isAccessibleContainer(thing), `Expected ${thing} to be accessible`);
    return thing;
  }

  function getColorAndTooltip(
    cell: GridCell | null,
    rowIndex: number,
    columnIndex: number
  ): {color: string; tooltip: string} {
    const coordinates = formatGridCoordinates(rowIndex, columnIndex);

    if (cell == null) {
      return {color: GRAY_LIGHTER, tooltip: `${coordinates}: (No container)`};
    } else if (isInaccessible(cell.container)) {
      return {color: GRAY, tooltip: `${coordinates}: (No access)`};
    }

    let color;
    let tooltip;

    const container = accessibleContainer(cell.container);
    const checkOutStatusInfo: CheckOutStatusInfo | null = container.checkOutStatusInfo;

    if (container.contents.length === 0) {
      tooltip = `${coordinates}: (Empty)`;
      color = WHITE;
    } else {
      const quantityStr = getLegacyMeasurementString(container.quantityJson, '(No quantity)');
      const contentsStr = getNonBatchContainerContents<ContainerContent, ContentWithoutBatches>(
        container.contents
      )
        .map(({containedItem}) => (isInaccessible(containedItem) ? '(No access)' : containedItem.name))
        .join(', ');
      const concentrationsStr = container.contents
        .map(({concentrationJson: concentration}) =>
          getLegacyMeasurementString(concentration, '(no concentration)')
        )
        .join(', ');
      tooltip = `${coordinates}: ${contentsStr}\n${quantityStr} @ ${concentrationsStr}`;
      color = GREEN_LIGHTER;
    }

    const isExpired =
      BenchlingConfig.INVENTORY_ENABLE_EXPIRATION_DATE &&
      container.expirationInfo != null &&
      container.expirationInfo.isTrackingEnabled &&
      container.expirationInfo.isExpired;
    if (isExpired) {
      tooltip = `${tooltip}\nExpired`;
    }

    if (checkOutStatusInfo != null && checkOutStatusInfo.status !== CheckOutStatus.AVAILABLE) {
      tooltip = `${tooltip}\n${getCICOWarningText(checkOutStatusInfo, {hideAssignee: false})}`;
    }

    const sampleStatusItem: SampleStatusItem = omit(container, 'expirationInfo');
    const isUnavailable = !isItemAvailable(sampleStatusItem);
    const isUnauthorized =
      hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1) &&
      !doesCurrentUserHaveAccessForItem(sampleStatusItem);
    const hasStatusWarning = isUnavailable || isUnauthorized || isExpired;

    if (doesItemHaveBlockingStatusWarning(sampleStatusItem)) {
      color = RED_LIGHTEST;
    } else if (hasStatusWarning) {
      color = ORANGE_LIGHTER;
    }

    if (isUnauthorized) {
      tooltip = `${tooltip}\n${getRestrictionAccessDisplayText(sampleStatusItem)}`;
    }

    return {color, tooltip};
  }

  const isFixedPlate =
    isPlateSchemaGraphQL(props.grid.storableSchema) &&
    props.grid.storableSchema.plateSchemaType === PlateSchemaType.FIXED_PLATE;
  const cellWidth = (props.widthPx - 3) / props.grid.storableSchema.width;

  return (
    <svg
      width={props.widthPx}
      height={props.heightPx}
      {...getTestProps({component: 'GridPreview', element: 'root'})}
    >
      <rect
        x="0"
        y="0"
        width={props.widthPx}
        height={props.heightPx}
        fill={GRAY_LIGHT}
        {...getTestProps({component: 'GridPreview', element: 'outerRect'})}
      />
      {props.grid.cells.map((rowCells, rowIndex) =>
        rowCells.map((cell, columnIndex) => {
          const {color, tooltip} = getColorAndTooltip(cell, rowIndex, columnIndex);
          const positionKey = `r${rowIndex}c${columnIndex}`;
          return (
            <Tooltip key={`cell-r${rowIndex}c${columnIndex}`} text={tooltip}>
              <g
                aria-label={tooltip}
                {...getTestProps({component: 'GridPreview', element: 'cell', key: positionKey})}
              >
                {isFixedPlate ? null : (
                  <rect
                    x={1.5 + cellWidth * (columnIndex + 0.5) - cellWidth * 0.475}
                    y={1.5 + cellWidth * (rowIndex + 0.5) - cellWidth * 0.475}
                    width={cellWidth * 0.95}
                    height={cellWidth * 0.95}
                    fill={GRAY_LIGHTER}
                    {...getTestProps({component: 'GridPreview', element: 'cellSquare', key: positionKey})}
                  />
                )}
                {cell != null ? (
                  <a
                    onClick={() => {
                      FileBrowserActions.openFile(getAccessibleOrInaccessibleId(cell.container));
                    }}
                    {...getTestProps({component: 'GridPreview', element: 'containerLink', key: positionKey})}
                  >
                    <circle
                      cx={1.5 + cellWidth * (columnIndex + 0.5)}
                      cy={1.5 + cellWidth * (rowIndex + 0.5)}
                      r={0.4 * cellWidth}
                      fill={color}
                      {...getTestProps({
                        component: 'GridPreview',
                        element: 'containerCircle',
                        key: positionKey,
                      })}
                    />
                  </a>
                ) : null}
              </g>
            </Tooltip>
          );
        })
      )}
    </svg>
  );
}
